import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Card,
  Group,
  LoadingOverlay,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconCash,
  IconClipboardList,
  IconCoin,
  IconEdit,
  IconInfoCircle,
  IconRefresh,
  IconUsers,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { GroupDashboard, MyGroupEntry } from '@pta/types';
import { fetchGroupDashboard } from '../api/dashboard';
import { ApiError } from '../api/client';
import { formatTimestamp } from '../util/format';
import { useAuth } from '../auth/AuthProvider';
import { useMoneyFormatter } from '../util/money';
import { CashBalanceModal } from '../components/CashBalanceModal';
import { DashboardTimeseriesChart } from '../components/DashboardTimeseriesChart';

/**
 * Csoport-szintű dashboard.
 *
 * Pénzformázás: a csoport `GroupLocale`-jából (useMoneyFormatter).
 * A primary currency mindig a csoporté, secondary tájékoztató currency-t
 * a számolás után magunk formázzuk (más decimal/symbol mert eltérő pénznem).
 */
export function FinancialDashboard() {
  const navigate = useNavigate();
  const { user, activeRole, myGroups, activeGroupId } = useAuth();
  const { money } = useMoneyFormatter();
  const activeGroup = useMemo(
    () => myGroups.find((g) => g.id === activeGroupId) ?? null,
    [myGroups, activeGroupId],
  );

  const [data, setData] = useState<GroupDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();

  const canEditCashBalance =
    activeRole === 'pta_cashier' ||
    activeRole === 'institution_admin' ||
    activeRole === 'country_admin' ||
    activeRole === 'systemadmin' ||
    activeRole === 'applicationadmin';

  const load = async () => {
    if (activeGroupId === null) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchGroupDashboard(activeGroupId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [activeGroupId]);

  if (activeGroupId === null) {
    return (
      <Stack gap="md">
        <Title order={2}>Kezdőlap</Title>
        <Text>
          Üdv, {user?.displayName ?? 'felhasználó'}! Aktív szerep:{' '}
          <strong>{activeRole}</strong>.
        </Text>
        {myGroups.length === 0 ? (
          <Alert color="yellow" title="Nincs csoport">
            Még nem tartozol egyetlen csoporthoz sem.
          </Alert>
        ) : (
          <Alert color="yellow" icon={<IconInfoCircle size={18} />}>
            Nincs aktív csoport kiválasztva. A fejléc bal oldalán válassz egyet.
          </Alert>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={loading} />

      <Group justify="space-between" align="end">
        <Stack gap={2}>
          <Title order={2}>{activeGroup?.name ?? `#${activeGroupId}`}</Title>
          <Text size="sm" c="dimmed">
            {describeInstitution(activeGroup)} · szerep: <strong>{activeRole}</strong>
          </Text>
        </Stack>
        <Group gap="xs">
          {data && (
            <Text size="xs" c="dimmed">
              Frissítve: {formatTimestamp(data.generatedAt)}
            </Text>
          )}
          <Tooltip label="Frissítés">
            <ActionIcon onClick={() => void load()} variant="light">
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {/* Diákok */}
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Diákok</Text>
            <IconUsers size={20} />
          </Group>
          {data && (
            <>
              <Text size="xl" fw={700}>
                {data.students.activeCount}
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                aktív tag
              </Text>
              <Group gap="xs">
                <Badge variant="light" color="gray">
                  informal: {data.students.byIdentityStatus.informal}
                </Badge>
                <Badge variant="light" color="blue">
                  identified: {data.students.byIdentityStatus.identified}
                </Badge>
                <Badge variant="light" color="teal">
                  verified: {data.students.byIdentityStatus.verified}
                </Badge>
              </Group>
            </>
          )}
        </Card>

        {/* Előírások */}
        <Card
          withBorder
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/app/requirements')}
        >
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Előírások</Text>
            <IconClipboardList size={20} />
          </Group>
          {data && (
            <>
              <Text size="xl" fw={700}>
                {data.requirements.byStatus.published}
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                publikált (aktív)
              </Text>
              <Group gap="xs">
                <Badge variant="light" color="gray">
                  draft: {data.requirements.byStatus.draft}
                </Badge>
                <Badge variant="light" color="blue">
                  closed: {data.requirements.byStatus.closed}
                </Badge>
                <Badge variant="light" color="red">
                  cancelled: {data.requirements.byStatus.cancelled}
                </Badge>
              </Group>
            </>
          )}
        </Card>

        {/* Kassza */}
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Group gap={6}>
              <IconCash size={20} />
              <Text fw={600}>Kassza</Text>
            </Group>
            {canEditCashBalance && data && (
              <Tooltip label="Szerkesztés">
                <ActionIcon variant="subtle" onClick={openModal}>
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          {data &&
            (data.cashBalance ? (
              <Stack gap={4}>
                <BalanceLine
                  label="Készpénz (nyitó)"
                  amount={data.cashBalance.cashAmount}
                  secondary={data.currencyConfig.secondary}
                  rate={data.currencyConfig.secondaryRate}
                />
                <BalanceLine
                  label="Bank (nyitó)"
                  amount={data.cashBalance.bankAmount}
                  secondary={data.currencyConfig.secondary}
                  rate={data.currencyConfig.secondaryRate}
                />
                <BalanceLine
                  label="Nyitó összesen"
                  amount={data.cashBalance.total}
                  secondary={data.currencyConfig.secondary}
                  rate={data.currencyConfig.secondaryRate}
                />
                <Text size="xs" c="dimmed" mt={2}>
                  + befizetés {money(data.finance.paymentsTotal)} − kiadás{' '}
                  {money(data.finance.expensesTotal)}
                </Text>
                <BalanceLine
                  label="Aktuális egyenleg"
                  amount={data.finance.currentBalance}
                  secondary={data.currencyConfig.secondary}
                  rate={data.currencyConfig.secondaryRate}
                  bold
                />
                <Text size="xs" c="dimmed">
                  vonatkozási dátum: {data.cashBalance.asOfDate}
                </Text>
              </Stack>
            ) : (
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  Még nincs rögzített nyitóegyenleg.
                </Text>
                {data.finance.paymentsTotal !== '0.00' || data.finance.expensesTotal !== '0.00' ? (
                  <BalanceLine
                    label="Aktuális egyenleg (nyitó nélkül)"
                    amount={data.finance.currentBalance}
                    secondary={data.currencyConfig.secondary}
                    rate={data.currencyConfig.secondaryRate}
                    bold
                  />
                ) : null}
                {!canEditCashBalance && (
                  <Text size="xs" c="dimmed">
                    Csak pénztáros (cashier) tud rögzíteni.
                  </Text>
                )}
              </Stack>
            ))}
        </Card>

        {/* Nyitott követelés és prognózis */}
        <Card withBorder>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Nyitott követelés és prognózis</Text>
            <IconCoin size={20} />
          </Group>
          {data && (
            <Stack gap={4}>
              <BalanceLine
                label="Most (kassza)"
                amount={data.cashBalance?.total ?? '0'}
                secondary={data.currencyConfig.secondary}
                rate={data.currencyConfig.secondaryRate}
              />
              <BalanceLine
                label="Várható (összes beérkezik)"
                amount={data.outstanding.forecastTotal}
                secondary={data.currencyConfig.secondary}
                rate={data.currencyConfig.secondaryRate}
                bold
              />
              <Text size="xs" c="dimmed" mt={4}>
                Nyitott követelés: {money(data.outstanding.outstandingTotal)} (
                {data.outstanding.requirementCount} előírás,{' '}
                {data.outstanding.billableTargetCount} target
                {data.outstanding.targetCount !== data.outstanding.billableTargetCount &&
                  `, ${data.outstanding.targetCount - data.outstanding.billableTargetCount} mentesítve`}
                )
              </Text>
            </Stack>
          )}
        </Card>

        {/* Befizetés-státusz placeholder */}
        <Card withBorder style={{ opacity: 0.65 }}>
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Befizetés-státusz</Text>
            <IconClipboardList size={20} />
          </Group>
          <Text size="sm" c="dimmed">
            F2d (tudomásulvétel) + F2e (befizetés) után aktív.
          </Text>
        </Card>
      </SimpleGrid>

      {data && (
        <DashboardTimeseriesChart
          groupId={activeGroupId}
          primaryCurrency={data.currencyConfig.primary}
        />
      )}

      {data && (
        <CashBalanceModal
          opened={modalOpened}
          onClose={closeModal}
          groupId={activeGroupId}
          primaryCurrency={data.currencyConfig.primary}
          initial={
            data.cashBalance
              ? {
                  cashAmount: data.cashBalance.cashAmount,
                  bankAmount: data.cashBalance.bankAmount,
                  asOfDate: data.cashBalance.asOfDate,
                  notes: data.cashBalance.notes,
                }
              : null
          }
          onSaved={async () => {
            closeModal();
            await load();
          }}
        />
      )}
    </Stack>
  );
}

interface BalanceLineProps {
  label: string;
  amount: string;
  secondary: string | null;
  rate: string | null;
  bold?: boolean;
}

/**
 * Pénz-sor: a primary összeget a csoport locale-ja szerint formázzuk
 * (useMoneyFormatter), a tájékoztató secondary-t maga számolva ki.
 *
 * A secondary saját formátum-szabályát NEM töltjük be külön currency-ből
 * (multi-currency F3+); csak `Number.toFixed(2)` + symbol nélküli code.
 */
function BalanceLine({ label, amount, secondary, rate, bold }: BalanceLineProps) {
  const { money } = useMoneyFormatter();
  const secondaryText =
    secondary && rate
      ? `≈ ${(Number(amount) * Number(rate)).toFixed(2)} ${secondary}`
      : secondary && !rate
        ? `${secondary}: nincs árfolyam`
        : null;

  return (
    <Group justify="space-between" gap="xs">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Stack gap={0} align="flex-end">
        <Text fw={bold ? 700 : 500}>{money(amount)}</Text>
        {secondaryText && (
          <Text size="xs" c="dimmed">
            {secondaryText}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

function describeInstitution(g: MyGroupEntry | null): string {
  if (!g) return '';
  if (g.institutionLabel === null) return 'ismeretlen iskola';
  if (g.institutionBindingStatus === 'free_text') {
    return `${g.institutionLabel} (szabad szövegesen)`;
  }
  return g.institutionLabel;
}
